import React, { useEffect, useMemo, useState } from "react";

import { useAdminUserContext, useCreateVersionReview, useVersionReviews } from "utils";

import { Flex, Message, Modal, Option, Select, TextArea, Typography } from "components";

const SCORE_OPTIONS = Array.from({ length: 11 }, (_, i) => i);

type SingleVersionCommentModalProps = {
  open: boolean;
  onClose: () => void;
  version: string;
};

// Stub comment box for KH-746 — reuses the review-pair endpoint with baselineVersion === candidateVersion
// as a convention for "single version" reviews. Not anchored to any row/table/chart.
// Superseded once KH-745 ships general anchored comments — do not extend this pattern further.
export const SingleVersionCommentModal = ({ open, onClose, version }: SingleVersionCommentModalProps) => {
  const { user } = useAdminUserContext();
  const { data: versionReviews } = useVersionReviews();
  const { mutate: createReview, isPending: isCreateReviewPending } = useCreateVersionReview();

  const existingReview = useMemo(
    () =>
      versionReviews?.find(
        (versionReview) => versionReview.reviewerId === user?.id && versionReview.baselineVersion === version && versionReview.candidateVersion === version
      ) ?? null,
    [versionReviews, user?.id, version]
  );
  const existingCurrent = existingReview?.revisions?.[0] ?? null;

  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (!open) return;
    setScore(existingCurrent?.score ?? null);
    setComment(existingCurrent?.comment ?? "");
  }, [open, existingCurrent]);

  const handleSubmit = () => {
    if (score === null) return;
    createReview(
      { baselineVersion: version, candidateVersion: version, score, comment: comment || null },
      {
        onSuccess: () => {
          Message({
            content: existingReview ? `Review updated for ${version}` : `Review submitted for ${version}`,
            icon: "tick",
          });
          onClose();
        },
        onError: () => Message({ content: "Could not save the review. Please try again.", icon: "error" }),
      }
    );
  };

  return (
    <Modal
      title={existingReview ? "Update review" : "Review this version"}
      showModal={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={existingReview ? "Update review" : "Submit review"}
      okButtonProps={{ disabled: score === null || isCreateReviewPending, loading: isCreateReviewPending }}
      content={
        <Flex vertical gap="1rem" className="padding-100">
          <Typography variant="paragraph-m">{version}</Typography>
          <Select label="Score (0 = unusable · 5 = acceptable with caveats · 10 = ship it)" value={score ?? undefined} onChange={setScore}>
            {SCORE_OPTIONS.map((scoreOption) => (
              <Option key={scoreOption} value={scoreOption}>
                {scoreOption}
              </Option>
            ))}
          </Select>
          <TextArea
            key={`${version}:${existingCurrent?.id ?? "new"}`}
            name="comment"
            label="Comment"
            placeholder="Why this score? Anything the silver-service team should adapt?"
            rows={4}
            initialValue={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Flex>
      }
    />
  );
};
